interface LocationLike {
  pathname: string;
  search?: string;
  hash?: string;
}

// The patient upload portal (ESCIQ-198) lives at a fixed top-level path so its URL never names a
// client or drug; its API is the fixed /api/v1/patient-portal namespace.
export const PATIENT_PORTAL_SEGMENT = 'p';
const PATIENT_PORTAL_API_BASE = '/api/v1/patient-portal';

export interface PortalPathInfo {
  clientName: string;
  programName: string;
  basename: string;
  appPath: string;
}

export function getPortalPathInfo(pathname: string = window.location.pathname): PortalPathInfo {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] === PATIENT_PORTAL_SEGMENT) {
    return {
      clientName: '',
      programName: '',
      basename: `/${PATIENT_PORTAL_SEGMENT}`,
      appPath: segments.length > 1 ? `/${segments.slice(1).join('/')}` : '/',
    };
  }
  const clientName = segments[0] ?? '';
  const programName = segments[1] ?? '';
  const basename = clientName && programName ? `/${clientName}/${programName}` : '/';
  const appSegments = segments.slice(2);
  const appPath = appSegments.length > 0 ? `/${appSegments.join('/')}` : '/';

  return {
    clientName,
    programName,
    basename,
    appPath,
  };
}

export function getPortalApiBase(pathname: string = window.location.pathname): string {
  const { basename } = getPortalPathInfo(pathname);

  if (basename === `/${PATIENT_PORTAL_SEGMENT}`) {
    return PATIENT_PORTAL_API_BASE;
  }

  return basename === '/' ? '/api/v1' : `/api/v1${basename}`;
}

export function buildReturnToPath(location: LocationLike): string {
  return `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
}

export function normalizeReturnToPath(returnTo: string | null | undefined): string | null {
  if (!returnTo) {
    return null;
  }

  const normalized = returnTo.trim();
  if (!normalized.startsWith('/') || normalized.startsWith('//')) {
    return null;
  }

  return normalized;
}

export function buildPortalDestination(returnTo: string | null | undefined, pathname: string = window.location.pathname): string {
  const { basename } = getPortalPathInfo(pathname);
  const normalizedReturnTo = normalizeReturnToPath(returnTo);

  if (!normalizedReturnTo || normalizedReturnTo === '/') {
    return basename;
  }

  return `${basename}${normalizedReturnTo}`;
}